import { useEffect, useRef } from "react";
import { useAppContext } from "./useAppContext";
import { useConfigs } from "./useConfigs";
import type { Metric } from "web-vitals";

/** Log resource that are large or take long to load, and monitor web vitals */
export function usePerformanceMonitoring() {
	const { logger, googleAnalytics, context, editor } = useAppContext();

	/**
	 * - undefined: no yet initialized
	 * - true/false: should load and log or not
	 * - Prevent double initialization in React StrictMode/development
	 */
	const logWebVitalsRef = useRef<boolean | undefined>(undefined);

	const [
		[logWebVitalPercent = "1", logFetchLargerThanMb = "5", logFetchSlowerThanSeconds = "20"],
		isLoadingConfigs,
	] = useConfigs([
		{
			key: "Mobile.Performance.LogWebVitalPercent",
		},
		{
			key: "Mobile.Performance.LogFetchLargerThanMb",
		},
		{
			key: "Mobile.Performance.LogFetchSlowerThanSeconds",
		},
	]);

	// Initialize web-vitals monitoring (prevent double initialization in StrictMode)
	useEffect(() => {
		if (logWebVitalsRef.current === false) return;
		if (isLoadingConfigs) return;

		if (logWebVitalsRef.current === undefined) {
			// Set once
			if (logWebVitalPercent !== undefined) {
				logWebVitalsRef.current = Math.random() * 100 < parseFloat(logWebVitalPercent);
			}
		}

		// Skip if already initialized to prevent double logging in React StrictMode
		if (!logWebVitalsRef.current) return;

		const logWebVital = (metric: Metric) => {
			try {
				const { name, value, rating, delta, id, navigationType } = metric;

				// Create structured data for logging
				const webVitalData = {
					metric: name,
					value: Math.round(value),
					rating,
					delta: Math.round(delta),
					id,
					navigationType,
					timestamp: new Date().toISOString(),
				};

				// Log to console via the logger
				const logType = rating === "good" ? "info" : "warn";
				logger[logType](
					`Web Vital - ${name}: ${Math.round(value)}ms (${rating})\n${JSON.stringify(
						webVitalData,
					)}`,
				);

				// Log to GA
				const { definition } = editor;

				googleAnalytics.pushToDataLayer({
					event: `creator-webvitals-${name?.toLowerCase()}`,
					value: Math.round(value),
					rating,
					delta: Math.round(delta),
					id,
					navigationType,
					editor: "MobileCreator",
					accountId: context.account.id,
					pvc: definition?.getSelectedProductVariant()?.code || undefined,
					ppn: definition?.product.predefinedName || undefined,
				});
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : String(err);
				logger.warn(`Failed to log web vital ${metric.name}: ${errorMessage}`);
			}
		};

		// Initialize web-vitals using dynamic import
		const initializeWebVitals = async () => {
			try {
				// Mark as initialized before starting to prevent race conditions
				logWebVitalsRef.current = true;

				// Dynamically import web-vitals
				const webVitals = await import(
					/* webpackChunkName: "express-web-vitals" */
					"web-vitals"
				);

				// Monitor Core Web Vitals
				webVitals.onCLS?.(logWebVital);
				webVitals.onFCP?.(logWebVital);
				webVitals.onINP?.(logWebVital);
				webVitals.onLCP?.(logWebVital);
				webVitals.onTTFB?.(logWebVital);

				logger.info("Web Vitals monitoring enabled");
			} catch (err) {
				// Reset flag on error so it can be retried
				logWebVitalsRef.current = false;
				const errorMessage = err instanceof Error ? err.message : String(err);
				logger.warn(`Failed to initialize web-vitals: ${errorMessage}`);
			}
		};

		// Run the async initialization (void to indicate intentionally not awaited)
		void initializeWebVitals();
	}, [context, editor, googleAnalytics, isLoadingConfigs, logWebVitalPercent, logger]);

	useEffect(() => {
		// https://web.dev/custom-metrics/#resource-timing-api
		// Catch errors since some browsers throw when using the new `type` option.
		// https://bugs.webkit.org/show_bug.cgi?id=209216
		let po: PerformanceObserver | undefined;
		try {
			po = new PerformanceObserver((list) => {
				for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
					// Skip checks on our javascript sizes
					if (entry.name.endsWith(".js")) return;

					if (
						logFetchLargerThanMb &&
						parseFloat(logFetchLargerThanMb) * 1024 * 1024 < entry.transferSize
					) {
						logger.warn(
							`Performance warning: ${entry.name}: ${
								Math.round(entry.transferSize / 1024 / 102.4) / 10
							}MB`,
						);

						const { definition } = editor;
						googleAnalytics.pushToDataLayer({
							event: `creator-large-fetch`,
							value: entry.transferSize,
							minLogValue: parseFloat(logFetchLargerThanMb) * 1024 * 1024,
							name: entry.name,
							initiatorType: entry.initiatorType,
							editor: "MobileCreator",
							accountId: context.account.id,
							pvc: definition?.getSelectedProductVariant()?.code || undefined,
							ppn: definition?.product.predefinedName || undefined,
						});
					}

					if (
						logFetchSlowerThanSeconds &&
						entry.duration > parseFloat(logFetchSlowerThanSeconds) * 1000
					) {
						const size =
							entry.transferSize === 0
								? ", from cache"
								: `, ${Math.ceil(entry.transferSize / 1024)}KB`;
						const initiatorType = entry.initiatorType
							? `, initiatorType: ${entry.initiatorType}`
							: "";

						logger.warn(
							`Performance warning: ${entry.name}: Took ${Math.ceil(
								entry.duration,
							)}ms to load${size}${initiatorType}`,
						);

						const { definition } = editor;
						googleAnalytics.pushToDataLayer({
							event: `creator-slow-fetch`,
							value: entry.duration,
							minLogValue: parseFloat(logFetchSlowerThanSeconds) * 1000,
							name: entry.name,
							initiatorType: entry.initiatorType,
							editor: "MobileCreator",
							accountId: context.account.id,
							pvc: definition?.getSelectedProductVariant()?.code || undefined,
							ppn: definition?.product.predefinedName || undefined,
						});
					}
				}
			});
			po.observe({ type: "resource", buffered: true });
		} catch (_e) {
			// Do nothing if the browser doesn't support this API.
		}

		return () => {
			po?.disconnect();
		};
	}, [
		context.account.id,
		editor,
		googleAnalytics,
		logFetchLargerThanMb,
		logFetchSlowerThanSeconds,
		logger,
	]);
}
